import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, Form, Spinner, Alert } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { goldenFastAPI } from 'features/vps/vpsGoldenFastAPI';

const VpsProductSelector = ({ 
  onProductChange, 
  selectedGroupId, 
  selectedProductId, 
  onGroupChange 
}) => {
  const [productGroups] = useState([
    { id: '38', name: 'Dedicated Server' },
    { id: '4', name: 'Colocation Server' },
    { id: '26', name: 'Cloud VPS Corporate' },
    { id: '27', name: 'Leased To Owned Dedicated Bare Metal Server' },
    { id: '29', name: 'Cloud VPS corporate - JKT2' },
    { id: '31', name: 'Cloud VPS corporate - WestJavaBDG-1' }
  ]);
  
  const [products, setProducts] = useState([]);
  const [productDetail, setProductDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isUsingMockData, setIsUsingMockData] = useState(false);

  // Use refs to avoid dependency issues
  const selectedProductIdRef = useRef(selectedProductId);
  const onProductChangeRef = useRef(onProductChange);
  
  // Update refs when props change
  useEffect(() => {
    selectedProductIdRef.current = selectedProductId;
    onProductChangeRef.current = onProductChange;
  }, [selectedProductId, onProductChange]);

  const loadProductsByGroup = useCallback(async (groupId) => {
    setLoading(true);
    setError(null);
    setProducts([]); // Reset products list
    setIsUsingMockData(false);
    
    try {
      // Call API untuk mendapatkan produk berdasarkan grup
      const apiResponse = await goldenFastAPI.loadProductsByGroup(groupId);
      
      // Check if we're using mock data
      const isMockData = apiResponse.data && apiResponse.data.some(item => item.id === 999);
      setIsUsingMockData(isMockData);
      
      if (apiResponse.status && apiResponse.data && Array.isArray(apiResponse.data)) {
        // Transform API response to match expected format
        const transformedProducts = apiResponse.data.map(item => ({
          pid: (item.pid || item.id).toString(),
          name: item.name,
          id: item.id
        }));
        
        // Filter products based on hidden PID rules
        const filteredProducts = goldenFastAPI.filterProducts(transformedProducts);
        
        setProducts(filteredProducts);
      } else {
        setProducts([]);
        
        // Show informative message
        const groupInfo = goldenFastAPI.getGroupInfo(groupId);
        setError(`Tidak ada produk ditemukan untuk grup "${groupInfo.name}"`);
      }
    } catch (err) {
      // Enhanced error messages for different error types
      let errorMessage = 'Gagal memuat produk dari grup';
      
      if (err.message.includes('CORS')) {
        errorMessage = 'CORS Error: Akses ke API terblokir.';
      } else if (err.message.includes('Network')) {
        errorMessage = 'Network Error: Tidak dapat terhubung ke API.';
      } else if (err.message.includes('fetch')) {
        errorMessage = 'Fetch Error: Gagal mengambil data.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Failed to Fetch: Tidak dapat mengambil data dari server.';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      setProducts([]);
    } finally {
      setLoading(false);
    }
  }, []);



  // Load products when group changes
  useEffect(() => {
    if (selectedGroupId) {
      loadProductsByGroup(selectedGroupId);
    } else {
      setProducts([]);
      setProductDetail(null);
      setError(null);
      setIsUsingMockData(false);
    }
  }, [selectedGroupId, loadProductsByGroup]);

  // Reset selected product when group changes
  useEffect(() => {
    if (selectedProductIdRef.current && onProductChangeRef.current) {
      setProductDetail(null);
      onProductChangeRef.current(null);
    }
  }, [selectedGroupId]); // Only depend on selectedGroupId change



  const handleGroupChange = (e) => {
    const groupId = e.target.value;
    if (onGroupChange) {
      onGroupChange(groupId);
    }
  };

  const handleProductChange = async (e) => {
    const productId = e.target.value;
    
    if (!productId) {
      // Reset when no product selected
      setProductDetail(null);
      if (onProductChangeRef.current) {
        onProductChangeRef.current(null);
      }
      return;
    }

    // Load product detail directly when product is selected
    try {
      setLoading(true);
      setError(null);
      
      const response = await goldenFastAPI.loadProductAddons(productId);
      
      if (response.status && response.data && response.data.length > 0) {
        const product = response.data[0];
        
        // Ensure addons array exists
        if (!product.addons) {
          product.addons = [];
        }
        
        setProductDetail(product);
        
        // Notify parent component using ref
        if (onProductChangeRef.current) {
          onProductChangeRef.current(product);
        }
      } else {
        setError(`Detail produk dengan ID ${productId} tidak ditemukan`);
        
        // Notify parent component with null using ref
        if (onProductChangeRef.current) {
          onProductChangeRef.current(null);
        }
      }
    } catch (err) {
      // Enhanced error messages for product detail loading
      let errorMessage = 'Gagal memuat detail produk';
      
      if (err.message.includes('CORS')) {
        errorMessage = 'CORS Error: Tidak dapat mengakses API addon. Menggunakan data demo.';
      } else if (err.message.includes('Network')) {
        errorMessage = 'Network Error: Gagal terhubung ke API addon. Menggunakan data demo.';
      } else if (err.message.includes('Failed to fetch')) {
        errorMessage = 'Failed to Fetch: Tidak dapat mengambil data addon dari server. Menggunakan data demo.';
      } else {
        errorMessage = `Error: ${err.message}`;
      }
      
      setError(errorMessage);
      
      // Notify parent component with null on error using ref
      if (onProductChangeRef.current) {
        onProductChangeRef.current(null);
      }
    } finally {
      setLoading(false);
    }
  };

  const renderProductSpecifications = () => {
    if (!productDetail || !productDetail.description) return null;

    const specifications = goldenFastAPI.parseProductDescription(productDetail.description);

    return (
      <div className="mt-3">
        <div className="row">
          <div className="col-md-12">
            <ul className="list-unstyled">
              {specifications.map((spec, index) => (
                <li key={index} className="d-flex align-items-center mb-2">
                  <FontAwesomeIcon 
                    icon={faCheck} 
                    className="text-success me-2" 
                    style={{ fontSize: '14px' }} 
                  />
                  <span className="text-muted small">
                    {spec.isEasyManaged ? (
                      <button 
                        type="button"
                        className="btn btn-link p-0 text-decoration-none"
                        data-bs-toggle="modal" 
                        data-bs-target="#modalEasyManaged"
                        style={{ color: 'inherit' }}
                      >
                        {spec.text}
                      </button>
                    ) : (
                      <strong>{spec.text}</strong>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>
    );
  };

  const renderPricingInfo = () => {
    if (!productDetail || !productDetail.pricing) return null;

    const pricing = productDetail.pricing.IDR;
    if (!pricing) return null;

    return (
      <div className="mt-3">
        <h6 className="fw-bold text-qw-primary">Harga Mulai Dari:</h6>
        <div className="d-flex flex-wrap gap-2">
          {pricing.monthly && pricing.monthly !== '-1.00' && (
            <span className="badge bg-light text-dark border">
              Bulanan: {goldenFastAPI.formatRupiah(pricing.monthly)}
            </span>
          )}
          {pricing.annually && pricing.annually !== '-1.00' && (
            <span className="badge bg-primary">
              Tahunan: {goldenFastAPI.formatRupiah(pricing.annually)}
            </span>
          )}
        </div>
      </div>
    );
  };

  return (
    <Card className="rounded-4 border-0 shadow-sm mb-4">
      <Card.Body>
        <div className="row">
          <div className="col-12">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="fw-bold text-qw-primary mb-0">
                Pilih Grup Produk
                {selectedGroupId && (
                  <small className="text-muted ms-2">
                    (ID: {selectedGroupId})
                  </small>
                )}
              </h5>
            </div>
            
            {/* Group Selector */}
            <Form.Select 
              value={selectedGroupId || ''} 
              onChange={handleGroupChange}
              className="mb-3"
              style={{ 
                border: '2px solid #e0e6ed',
                borderRadius: '8px',
                padding: '10px 16px'
              }}
            >
              <option value="">-- Pilih Grup Produk --</option>
              {productGroups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name}
                </option>
              ))}
            </Form.Select>

            {/* Product Selector */}
            {selectedGroupId && (
              <>
                <h5 className="fw-bold text-qw-primary mb-3 mt-4">Pilih Produk</h5>
                
                {loading && (
                  <div className="text-center py-3">
                    <Spinner animation="border" size="sm" className="me-2" />
                    <span>Memuat produk...</span>
                  </div>
                )}

                {!loading && products.length === 0 && selectedGroupId && !error && (
                  <Alert variant="info" className="mb-3">
                    <strong>Tidak ada produk tersedia</strong><br/>
                    {(() => {
                      const groupInfo = goldenFastAPI.getGroupInfo(selectedGroupId);
                      return `Grup "${groupInfo.name}" (ID: ${selectedGroupId}) tidak memiliki produk yang tersedia.`;
                    })()}
                  </Alert>
                )}

                {error && (
                  <Alert variant="danger" className="mb-3">
                    <strong>Error</strong><br/>
                    {error}
                  </Alert>
                )}

                {!loading && products.length > 0 && (
                  <>
                    <div className="text-success small mb-2">
                      ✅ {products.length} produk ditemukan untuk grup {selectedGroupId}
                      {isUsingMockData && <span className="text-info ms-2">(Data Demo)</span>}
                    </div>
                    <Form.Select 
                      value={selectedProductId || ''} 
                      onChange={handleProductChange}
                      className="mb-3"
                      style={{ 
                        border: '2px solid #e0e6ed',
                        borderRadius: '8px',
                        padding: '10px 16px'
                      }}
                    >
                      <option value="">-- Pilih Produk --</option>
                      {products.map(product => (
                        <option key={product.pid} value={product.pid}>
                          {product.name}
                        </option>
                      ))}
                    </Form.Select>
                  </>
                )}

                {/* Product Specifications */}
                {selectedProductId && productDetail && (
                  <div className="mt-4">
                    <h6 className="fw-bold text-secondary mb-3">Spesifikasi Produk</h6>
                    {renderProductSpecifications()}
                    {renderPricingInfo()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </Card.Body>

      {/* Easy Managed Modal */}
      <div className="modal fade" id="modalEasyManaged" tabIndex="-1" aria-hidden="true">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title fw-bold">Easy Managed</h5>
              <button 
                type="button" 
                className="btn-close" 
                data-bs-dismiss="modal" 
                aria-label="Close"
              ></button>
            </div>
            <div className="modal-body">
              <p className="mb-3">
                Layanan ini telah dilengkapi dengan layanan Easy Managed. Staff ahli kami akan 
                memberikan bantuan dalam bentuk pengelolaan sederhana, pemantauan, dan pengawasan 
                untuk penggunaan bandwidth, Ping, instalasi/reinstalasi, dsb. Anda kini dapat fokus 
                menjalankan website perusahaan tanpa memikirkan pengelolaan server.
              </p>
              
              <ul className="list-group list-group-flush">
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  24 x 7 x 365 Network Monitoring (Jakarta, Bandung, Yogyakarta & Surabaya)
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  24 x 7 x 365 Onsite Staff Monitoring (Jakarta)
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  24 x 7 x 365 Offsite Staff monitoring (Bandung, Yogyakarta & Surabaya)
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  Remote Hand for Reboot / Power On / Power OFF
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  Network Monitoring site for Client
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  99% Uptime
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  UPS Backup
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  FIRE Extinguisher
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  Air Conditioning Control
                </li>
                <li className="list-group-item px-0">
                  <FontAwesomeIcon icon={faCheck} className="text-success me-2" />
                  Fast Replacement Unit
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Card>
  );
};

export default VpsProductSelector; 